import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Alert,
  Box,
  Collapse,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import LocalTaxiIcon from "@mui/icons-material/LocalTaxi";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HowToRegIcon from "@mui/icons-material/HowToReg";

import {
  IPD_COLORS,
  IPD_SHAPE,
  getIpdSelectMenuProps,
  ipdElevatedInputSx,
  ipdLabelSx,
  ipdPageSx,
  switchSx,
  IpdButton,
  IpdPageHeader,
  IpdSectionCard,
} from "../../../ipd/utils";
import { registerEmergencyPatient } from "../../api/emergencyApi";
import EmergencyAbhaPanel from "../../components/EmergencyAbhaPanel";

const GENDERS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
  { value: "T", label: "Other" },
];

const ARRIVAL_MODES = [
  { value: "walk-in", label: "Walk-in", icon: DirectionsWalkIcon },
  { value: "ambulance", label: "Ambulance", icon: LocalTaxiIcon },
];

const EMPTY_PAYER = {
  provider: "",
  policy_number: "",
  tpa: "",
  scheme: "",
  corporate_sponsor: "",
};

function Field({ label, required, children }) {
  return (
    <Box>
      <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>
        {label}
        {required && <span style={{ color: IPD_COLORS.error.text }}> *</span>}
      </Typography>
      {children}
    </Box>
  );
}

function ArrivalModePicker({ value, onChange }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }} role="radiogroup" aria-label="Mode of arrival">
      {ARRIVAL_MODES.map((m) => {
        const active = value === m.value;
        const Icon = m.icon;
        return (
          <Box
            key={m.value}
            component="button"
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(m.value)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              py: 1.4,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 14,
              fontWeight: active ? 700 : 500,
              borderRadius: `${IPD_SHAPE.radiusMd}px`,
              border: `1px solid ${active ? IPD_COLORS.selectedBorder : IPD_COLORS.border}`,
              bgcolor: active ? IPD_COLORS.selectedBg : IPD_COLORS.cardBg,
              color: active ? IPD_COLORS.primary : IPD_COLORS.textSecondary,
              transition: "all .15s ease",
              "&:hover": { borderColor: IPD_COLORS.primary },
            }}
          >
            <Icon sx={{ fontSize: 18 }} />
            {m.label}
          </Box>
        );
      })}
    </Box>
  );
}

export default function RegisterPatient() {
  const history = useHistory();

  const [unidentified, setUnidentified] = useState(false);
  const [patientName, setPatientName] = useState("");
  const [identificationMark, setIdentificationMark] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [arrivalMode, setArrivalMode] = useState("walk-in");
  const [chiefComplaint, setChiefComplaint] = useState("");
  const [mlcFlag, setMlcFlag] = useState(false);
  const [showPayer, setShowPayer] = useState(false);
  const [payer, setPayer] = useState(EMPTY_PAYER);
  // Set when the Aadhaar/ABHA fast-path resolved an existing patient and the
  // clerk has not declined the reuse — the visit will attach to this record.
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [reusedName, setReusedName] = useState("");

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState("");
  const [registered, setRegistered] = useState(null);

  // The fast-path fills the form and, when the ABDM flow resolved an existing
  // patient (server-side dedup on Aadhaar/ABHA), offers to attach to that
  // record. It never overrides the manual paths — every field stays editable
  // and the reuse can be declined below.
  const applyAbha = useCallback((m) => {
    if (m.name) {
      setPatientName(m.name);
      setUnidentified(false);
    }
    if (m.gender) setGender(m.gender);
    if (m.age) setAge(m.age);
    if (m.patientId) {
      setSelectedPatientId(m.patientId);
      setReusedName(m.name || "");
    } else {
      // A fetch that resolved no existing record must not leave a prior match
      // attached — otherwise re-scanning a different, unregistered person would
      // fill their details over the form while the visit stayed attached to the
      // earlier patient's chart.
      setSelectedPatientId(null);
      setReusedName("");
    }
    setErrors({});
  }, []);

  const declineReuse = useCallback(() => {
    setSelectedPatientId(null);
    setReusedName("");
  }, []);

  // Editing any prefilled identity field by hand breaks the correspondence with
  // the auto-offered match, so the reuse is dropped and the visit falls back to
  // registering a new patient (the clerk can re-fetch to attach again on
  // purpose). This is what stops a clerk typing one person's details over a
  // form still silently attached to another person's chart.
  const clearReuseOnEdit = useCallback(() => {
    setSelectedPatientId((cur) => (cur ? null : cur));
    setReusedName((cur) => (cur ? "" : cur));
  }, []);

  const validate = () => {
    const e = {};
    if (!selectedPatientId && !unidentified && !patientName.trim()) {
      e.patientName = "Enter a name, or mark the patient as unidentified.";
    }
    if (unidentified && !identificationMark.trim()) {
      e.identificationMark =
        "Required when no name is recorded — something staff can recognise this patient by.";
    }
    if (!gender) e.gender = "Select a gender.";
    if (!chiefComplaint.trim()) {
      e.chiefComplaint = "Record why the patient has come, in their own words.";
    }
    if (age !== "" && (Number.isNaN(Number(age)) || Number(age) < 0 || Number(age) > 130)) {
      e.age = "Enter an age between 0 and 130.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    setFailure("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const visit = await registerEmergencyPatient({
        patientName: unidentified ? undefined : patientName.trim(),
        identificationMark: unidentified ? identificationMark.trim() : undefined,
        age,
        gender,
        arrivalMode,
        chiefComplaint: chiefComplaint.trim(),
        payer: showPayer ? payer : undefined,
        mlcFlag,
        patientId: selectedPatientId || undefined,
      });
      setRegistered(visit);
    } catch (err) {
      setFailure(err?.message || "Could not register the patient");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForAnother = () => {
    setRegistered(null);
    setUnidentified(false);
    setPatientName("");
    setIdentificationMark("");
    setAge("");
    setGender("");
    setArrivalMode("walk-in");
    setChiefComplaint("");
    setMlcFlag(false);
    setShowPayer(false);
    setPayer(EMPTY_PAYER);
    setSelectedPatientId(null);
    setReusedName("");
    setErrors({});
  };

  const payerFields = useMemo(
    () => [
      ["provider", "Insurance provider", "e.g. Star Health"],
      ["policy_number", "Policy number", "e.g. SH-1234567"],
      ["tpa", "TPA", "e.g. MediAssist"],
      ["scheme", "Scheme", "e.g. CGHS, ESIC"],
      ["corporate_sponsor", "Corporate sponsor", "e.g. Infosys Ltd."],
    ],
    []
  );

  /* ------------------------------------------------ registered confirmation */
  if (registered) {
    return (
      <Box sx={{ ...ipdPageSx, p: { xs: 2, md: 3 } }}>
        <IpdPageHeader eyebrow="Emergency Department" title="Patient registered" />
        <Box sx={{ maxWidth: 720, mx: "auto" }}>
          <Alert
            icon={<CheckCircleOutlineIcon />}
            severity="success"
            sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
          >
            <strong>{registered.display_name}</strong> is registered and ready for triage.
          </Alert>

          <IpdSectionCard title="Identifiers">
            <Box sx={{ display: "grid", gap: 1.25 }}>
              {[
                ["Visit number", registered.visit_number],
                ["Hospital identifier", registered.patient_id],
                ["Health-record link", registered.health_record_id],
                ...(registered.temp_label ? [["Temporary label", registered.temp_label]] : []),
                ...(registered.mlc_case_number
                  ? [["Medico-legal case", registered.mlc_case_number]] : []),
              ].map(([k, v]) => (
                <Box key={k} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>{k}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                    {v || "—"}
                  </Typography>
                </Box>
              ))}
            </Box>
          </IpdSectionCard>

          {registered.duplicate_warning && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
              {registered.duplicate_warning}
            </Alert>
          )}

          <Box sx={{ display: "flex", gap: 1.5, mt: 3 }}>
            <IpdButton onClick={() => history.push("/emergency_tracking_board")}>
              Go to the tracking board
            </IpdButton>
            <IpdButton variant="outlined" onClick={resetForAnother}>
              Register another patient
            </IpdButton>
          </Box>
        </Box>
      </Box>
    );
  }

  /* --------------------------------------------------------------- the form */
  return (
    <Box sx={{ ...ipdPageSx, p: { xs: 2, md: 3 } }}>
      <IpdPageHeader
        eyebrow="Emergency Department"
        title="Register Patient"
        description="The minimum needed to start care — everything else can be filled in later."
        actions={
          <IpdButton variant="outlined" onClick={() => history.push("/emergency_tracking_board")}>
            Cancel
          </IpdButton>
        }
      />

      <Box sx={{ maxWidth: 860, mx: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.25,
            px: 2,
            py: 1.5,
            borderRadius: `${IPD_SHAPE.radiusMd}px`,
            bgcolor: IPD_COLORS.info.bg,
            border: `1px solid ${IPD_COLORS.info.border}`,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18, color: IPD_COLORS.info.text, mt: 0.2 }} />
          <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textPrimary }}>
            Registration never blocks care. Payer details, duplicates and the rest of the
            paperwork can all be handled once the patient is being treated.
          </Typography>
        </Box>

        {failure && (
          <Alert severity="error" sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }} onClose={() => setFailure("")}>
            {failure}
          </Alert>
        )}

        <IpdSectionCard
          title="Patient identity"
          subtitle="A patient who cannot give a name is registered with an identification mark and a temporary label instead"
        >
          <EmergencyAbhaPanel
            onApply={applyAbha}
            taggingPatientId={selectedPatientId || undefined}
            disabled={submitting}
          />

          {selectedPatientId && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.25,
                px: 1.75,
                py: 1.5,
                mb: 2,
                borderRadius: `${IPD_SHAPE.radiusMd}px`,
                bgcolor: IPD_COLORS.success.bg,
                border: `1px solid ${IPD_COLORS.success.border}`,
              }}
            >
              <HowToRegIcon sx={{ fontSize: 20, color: IPD_COLORS.success.text, mt: 0.2 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: IPD_COLORS.success.text }}>
                  Attaching to an existing patient
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textPrimary }}>
                  {reusedName ? `${reusedName} · ` : ""}
                  {selectedPatientId} — the visit will be linked to this record instead of
                  creating a new one.
                </Typography>
              </Box>
              <IpdButton variant="outlined" size="small" onClick={declineReuse}>
                Register new instead
              </IpdButton>
            </Box>
          )}

          {!selectedPatientId && (
            <FormControlLabel
              sx={{ mb: 2, ml: 0 }}
              control={
                <Switch
                  checked={unidentified}
                  onChange={(e) => {
                    setUnidentified(e.target.checked);
                    setErrors({});
                  }}
                  sx={switchSx}
                />
              }
              label={
                <Box sx={{ ml: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    Patient cannot be identified
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
                    Unconscious, or unable to give a name
                  </Typography>
                </Box>
              }
            />
          )}

          <Box sx={{ display: "grid", gap: 2 }}>
            {!unidentified ? (
              <Field label="Full name" required>
                <TextField
                  value={patientName}
                  onChange={(e) => {
                    setPatientName(e.target.value);
                    clearReuseOnEdit();
                  }}
                  placeholder="e.g. Ramesh Iyer"
                  size="small"
                  fullWidth
                  error={!!errors.patientName}
                  helperText={errors.patientName}
                  sx={ipdElevatedInputSx}
                  inputProps={{ "aria-label": "Full name" }}
                />
              </Field>
            ) : (
              <Field label="Identification mark" required>
                <TextField
                  value={identificationMark}
                  onChange={(e) => setIdentificationMark(e.target.value)}
                  placeholder="Clothing, tattoos, distinguishing features — anything staff can recognise them by"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  error={!!errors.identificationMark}
                  helperText={errors.identificationMark}
                  sx={ipdElevatedInputSx}
                  inputProps={{ "aria-label": "Identification mark" }}
                />
              </Field>
            )}

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <Field label="Age">
                <TextField
                  value={age}
                  onChange={(e) => {
                    setAge(e.target.value);
                    clearReuseOnEdit();
                  }}
                  placeholder="Approximate is fine"
                  size="small"
                  fullWidth
                  type="number"
                  error={!!errors.age}
                  helperText={errors.age}
                  sx={ipdElevatedInputSx}
                  inputProps={{ min: 0, max: 130, "aria-label": "Age" }}
                />
              </Field>
              <Field label="Gender" required>
                <TextField
                  select
                  value={gender}
                  onChange={(e) => {
                    setGender(e.target.value);
                    clearReuseOnEdit();
                  }}
                  size="small"
                  fullWidth
                  error={!!errors.gender}
                  helperText={errors.gender}
                  sx={ipdElevatedInputSx}
                  SelectProps={{ displayEmpty: true, MenuProps: getIpdSelectMenuProps() }}
                  inputProps={{ "aria-label": "Gender" }}
                >
                  <MenuItem value="" disabled>Select</MenuItem>
                  {GENDERS.map((g) => (
                    <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>
                  ))}
                </TextField>
              </Field>
            </Box>
          </Box>
        </IpdSectionCard>

        <IpdSectionCard title="Arrival">
          <Box sx={{ display: "grid", gap: 2 }}>
            <Field label="Mode of arrival">
              <ArrivalModePicker value={arrivalMode} onChange={setArrivalMode} />
            </Field>
            <Field label="Chief complaint" required>
              <TextField
                value={chiefComplaint}
                onChange={(e) => setChiefComplaint(e.target.value)}
                placeholder="In the patient's or attendant's own words…"
                size="small"
                fullWidth
                multiline
                minRows={2}
                error={!!errors.chiefComplaint}
                helperText={errors.chiefComplaint}
                sx={ipdElevatedInputSx}
                inputProps={{ "aria-label": "Chief complaint" }}
              />
            </Field>
          </Box>
        </IpdSectionCard>

        <IpdSectionCard
          title="Medico-legal"
          subtitle="Assault, road accident, poisoning, or brought in dead"
        >
          <FormControlLabel
            sx={{ ml: 0 }}
            control={
              <Switch checked={mlcFlag} onChange={(e) => setMlcFlag(e.target.checked)} sx={switchSx} />
            }
            label={
              <Box sx={{ ml: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  Flag as a medico-legal case
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
                  Stamps a unique case number now. It can also be flagged later in the visit.
                </Typography>
              </Box>
            }
          />
        </IpdSectionCard>

        <IpdSectionCard
          title="Payer details"
          subtitle="Optional — never required to register or to begin treatment"
          actions={
            <Switch
              checked={showPayer}
              onChange={(e) => setShowPayer(e.target.checked)}
              sx={switchSx}
              inputProps={{ "aria-label": "Capture payer details now" }}
            />
          }
        >
          <Collapse in={showPayer} unmountOnExit>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              {payerFields.map(([key, label, placeholder]) => (
                <Field key={key} label={label}>
                  <TextField
                    value={payer[key]}
                    onChange={(e) => setPayer((p) => ({ ...p, [key]: e.target.value }))}
                    placeholder={placeholder}
                    size="small"
                    fullWidth
                    sx={ipdElevatedInputSx}
                    inputProps={{ "aria-label": label }}
                  />
                </Field>
              ))}
            </Box>
          </Collapse>
          {!showPayer && (
            <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
              Skipped. These can be captured at any point later in the visit.
            </Typography>
          )}
        </IpdSectionCard>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pb: 4 }}>
          <IpdButton variant="outlined" onClick={() => history.push("/emergency_tracking_board")}>
            Cancel
          </IpdButton>
          <IpdButton onClick={submit} loading={submitting} size="large">
            Register patient
          </IpdButton>
        </Box>
      </Box>
    </Box>
  );
}
